import { Paper, Switch, TextField, Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import dateformat from "dateformat";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout } from "$components";
import {
  DataTableWithContext,
  EnumDataRowType,
  EnumFilterTypes,
} from "$components/DataTable";
import { EMPTY_DATA } from "$containers/constants";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { getOps } from "$services/Op.service";
import { PreparationOrderStatus } from "$types/PreparationOrder";
import { PreparationType } from "$utils/typeConstants/EDB";
import {
  getPreparationOrderStatusLabelByRole,
  getPreparationOrderStatusOptions,
} from "$utils/typeConstants/PreparationOrder";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { RenderStatus } from "./RenderStatus";
import { appState } from "$reducers";
import { usePopin } from "$components/Popin/usePopin";
import { Parcel } from "./Parcel";
import { CacheInvalidationAction } from "$utils/cache";

const useMakeCell = (value, type = undefined) => {
  const navigate = useNavigate();
  const link = ({ edb }) => `/edbs/${edb._id}?tabpane=3`;
  const { isAdmin } = useAuth();

  return {
    type,
    link,
    value,
    onClick: (row) => {
      if (isAdmin()) {
        navigate(link(row));
      }
    },
  };
};

const getOpDateColor = (op: any) => {
  if (op?.edb?.preparationType === PreparationType.SUPPLY) {
    const datedisp = new Date(op?.edb?.dispositionDate);
    const currentDate = new Date();
    if (
      datedisp < currentDate &&
      op.status !== PreparationOrderStatus.COLLECTED
    )
      return "red";
  }
  return "";
};

function Preparation() {
  const { userRole } = useAuth();
  const [selectOp, setSelectOp] = useState();
  const [isActive, setisActive] = useState(true);
  const [keyword, setKeyword] = useState<string>("");
  const { jclasses } = useJStyles();
  const { filter: companySidebarFilter } = useSelector(appState);
  const { isAdmin } = useAuth();
  const { Popin, PopinProps, togglePopin } = usePopin();

  const params = useQueryParameters();

  const externalState = useMemo(() => {
    const returnState = {};

    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }
    const { page, rowsPerPage, ...filters } = returnState as any;

    return {
      filters: {
        ...filters,
        isActive,
        keyword,
        ...(isAdmin() &&
          companySidebarFilter?._id && {
            company: companySidebarFilter?._id,
          }),
      },
      pagination: { page, rowsPerPage },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, keyword, params, companySidebarFilter]);

  const handleSwitch = () => {
    setisActive(!isActive);
  };

  const headerData = [
    {
      id: "opEdb",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "N° OP, N° EDB",
        placeholder: "Par N° OP ou EDB",
        value: params.get("opEdb"),
      },
      minWidth: 170,
      cell: {
        ...useMakeCell(({ code, edb }) => {
          return { title: code, subtitle: edb.edbId };
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "opEntrepot",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "REF OP, PREP. ENTREPÔT",
        placeholder: "Par ref OP ou ref entrepôt",
        value: params.get("opEntrepot"),
      },
      minWidth: 200,
      cell: {
        ...useMakeCell(({ reference, refPreparationEntrepot }) => {
          return {
            title: reference,
            subtitle: refPreparationEntrepot || EMPTY_DATA,
            isLarge: true,
          };
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "project",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "projet",
        placeholder: "Par nom ou par code",
        value: params.get("project"),
      },
      minWidth: 150,
      cell: {
        ...useMakeCell(({ project }) => {
          return {
            title: project?.code,
            subtitle: project?.name || EMPTY_DATA,
          };
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "dockyard",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Chantier",
        placeholder: "Par nom ou par code",
        value: params.get("dockyard"),
      },
      minWidth: 150,
      cell: {
        ...useMakeCell(({ dockyard }) => {
          return {
            title: dockyard?.code || EMPTY_DATA,
            subtitle: dockyard?.name || EMPTY_DATA,
          };
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "dates",
      filter: {
        type: EnumFilterTypes.DATE,
        label: "Date de livraison souhaitée",
        value: params.get("dates"),
      },
      width: 180,
      cell: {
        ...useMakeCell((op) => (
          <span style={{ color: getOpDateColor(op) }}>
            {dateformat(new Date(op.edb?.dispositionDate), DEFAULT_DATE_FORMAT)}
          </span>
        )),
        isDisabled: !isActive,
      },
    },
    {
      id: "status",
      filter: {
        type: EnumFilterTypes.SELECT,
        value: ({ status }) =>
          getPreparationOrderStatusLabelByRole(userRole, status),
        styleFilter: { maxWidth: 250 },
        options: getPreparationOrderStatusOptions(userRole),
        label: "statut",
      },
      width: 180,
      cell: {
        value: (op) => (
          <RenderStatus
            op={op}
            togglePopin={togglePopin}
            setSelectOp={setSelectOp}
          />
        ),
        isDisabled: !isActive,
      },
    },
    /*
    {
      id: "synchronisation",
      filter: {
        type: EnumFilterTypes.SELECT,
        value: ({ status }) =>
          getPreparationOrderStatusLabelByRole(userRole, status),
        styleFilter: { maxWidth: 250 },
        options: getPreparationOrderStatusOptions(userRole),
        label: "todo",
      },
      maxWidth: 160,
      cell: {
        value: (op) => op._id,
        isDisabled: !isActive,
      },
    }, */
  ];

  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        title="Liste des préparations"
        contentClassName={jclasses.jLayoutCustom}
      >
        <Paper className={jclasses.jPaper}>
          <DataTableWithContext
            externalState={externalState}
            filterList={[
              <FormControlLabel
                key="active-preparations"
                label="Préparations actives"
                control={
                  <Switch
                    onChange={handleSwitch}
                    checked={isActive}
                    color="primary"
                  />
                }
              />,
              <Autocomplete
                key="preparation-keyword"
                options={[]}
                aria-readonly={false as boolean}
                getOptionLabel={(option: any) => option?.name || option}
                onInputChange={(e: any, value: any) => {
                  setKeyword(value);
                }}
                value={keyword}
                freeSolo
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    placeholder="Mot clef..."
                    variant="outlined"
                    size="medium"
                    style={{
                      minWidth: 250,
                      minHeight: 37,
                    }}
                  />
                )}
              />,
            ]}
            buttonList={[]}
            rowsPerPage={50}
            thead={headerData}
            queryFunction={getOps}
            queryFunctionName={CacheInvalidationAction.GET_OPS}
            data-testid="opsList"
          />
          <Popin {...PopinProps}>
            <Parcel op={selectOp} togglePopin={togglePopin} />
            {/* for this case exceptionnaly, i'll dupplicate the code because i don't want any dependancy */}
          </Popin>
        </Paper>
      </Layout>
    </Box>
  );
}

export default Preparation;
